import React, { ReactNode } from 'react';
import { Button } from '@/ui-components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/ui-components/ui/breadcrumb';
import { cn } from '@/lib/utils/cn';

interface SubPageLayoutProps {
  children: ReactNode;
  pageTitle: string;
  parentPageTitle?: string;
  parentPageCallback?: () => void;
  navigateBackCallback: () => void;
  icon?: ReactNode;
  trailing?: ReactNode;
  className?: string;
  dataOnboarding?: string;
}

export const SubPageLayout: React.FC<SubPageLayoutProps> = ({
  children,
  pageTitle,
  parentPageTitle,
  parentPageCallback,
  navigateBackCallback,
  icon,
  trailing,
  className,
  dataOnboarding
}) => {
  return (
    <div className="h-full bg-white flex flex-col overflow-hidden" data-onboarding={dataOnboarding}>
      {/* Header */}
      <div className="flex-shrink-0 z-30 bg-white px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
        <div className="flex items-center justify-between gap-4 max-w-7xl mx-auto w-full">
          {/* Navigation */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={navigateBackCallback} 
              className="h-8 w-8 sm:h-10 sm:w-10 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {icon && (
                <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-blue-50 border border-blue-100 flex-shrink-0">
                  <div className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">
                    {icon}
                  </div>
                </div>
              )}
              <div className="min-w-0">
                <Breadcrumb>
                  <BreadcrumbList>
                    {parentPageTitle && (
                      <>
                        <BreadcrumbItem className="hidden sm:inline-flex">
                          {parentPageCallback ? (
                            <BreadcrumbLink asChild>
                              <button type="button" onClick={parentPageCallback} className="text-gray-500 hover:text-gray-900 transition-colors">
                                {parentPageTitle}
                              </button>
                            </BreadcrumbLink>
                          ) : (
                            <span className="text-gray-500">{parentPageTitle}</span>
                          )}
                        </BreadcrumbItem>
                        <BreadcrumbSeparator className="hidden sm:inline-flex" />
                      </>
                    )}
                    <BreadcrumbItem className="min-w-0">
                      <BreadcrumbPage className="text-xl sm:text-xl font-semibold text-gray-900 truncate">
                        {pageTitle}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
            </div>
          </div>

          {/* Actions */}
          {trailing && (
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {trailing}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={cn("flex-1 overflow-y-auto w-full", className)}>
        {children}
      </div>
    </div>
  );
};
